import {
  LOGIN_MUTATION,
  MANAGER_LOGIN_MUTATION,
  LOGOUT_MUTATION,
  REFRESH_TOKEN_MUTATION,
  VALIDATE_TOKEN_QUERY
} from '../components/graphql/auth';
import { getBrowserFingerprint, getDeviceInfo } from '../utils/deviceInfo';
import { initializeWebSocket, terminateWebSocket, setOnAuthError, STORAGE_KEYS } from '../apollo';

class AuthService {
  constructor() {
    this.apolloClient = null;
    this.currentDeviceId = null;
    this.refreshTimer = null;

    // WebSocket 인증 에러 콜백 설정
    // 에러 타입에 따라 다르게 처리:
    // - NETWORK_ERROR: 토큰 유지 (handleAuthError에서 이미 처리됨)
    // - INVALID_TOKEN / TOKEN_EXPIRED / UNKNOWN: 갱신 시도 후 실패하면 로그아웃
    setOnAuthError(async (errorType = 'UNKNOWN') => {
      console.log('🔑 [AuthService] 인증 에러 콜백 호출됨:', { errorType });

      // 네트워크 에러는 handleAuthError에서 이미 필터링됨
      // 하지만 방어적으로 한 번 더 체크
      if (errorType === 'NETWORK_ERROR') {
        console.log('🌐 [AuthService] 네트워크 에러 - 토큰 유지, 재연결 대기');
        window.dispatchEvent(new CustomEvent('auth-network-error', {
          detail: { timestamp: Date.now() }
        }));
        return; // 로그아웃하지 않음
      }

      // INVALID_TOKEN, TOKEN_EXPIRED, UNKNOWN: 갱신 시도
      // accessToken만 무효하고 refreshToken은 유효할 수 있으므로 갱신 시도
      console.log('🔑 [AuthService] 토큰 갱신 시도:', { errorType });
      const result = await this.refreshToken();
      if (!result?.success) {
        console.log('🔑 [AuthService] 토큰 갱신 실패 - 로그아웃 처리');
        await this.logout();
        window.location.href = '/login';
      }
    });
  }

  /**
   * Apollo Client 설정
   */
  setApolloClient(client) {
    this.apolloClient = client;
  }

  /**
   * 관리자 로그인 (managerLogin mutation 사용)
   */
  async managerLogin(id, password) {
    if (!this.apolloClient) {
      throw new Error('Apollo Client not initialized');
    }

    try {
      // 1. 관리자 로그인 요청 (간단한 파라미터)
      const { data } = await this.apolloClient.mutate({
        mutation: MANAGER_LOGIN_MUTATION,
        variables: {
          id,
          userPw: password
        },
        fetchPolicy: 'no-cache'
      });

      // 2. 서버에서 반환한 deviceId 저장 (서버-클라이언트 일관성 유지)
      this.currentDeviceId = data.managerLogin.deviceId;
      localStorage.setItem(STORAGE_KEYS.DEVICE_ID, this.currentDeviceId);

      // 3. 토큰 및 사용자 정보 저장
      this.saveAuthData(data.managerLogin);

      // 4. 토큰 자동 갱신 설정
      this.startTokenRefreshTimer(data.managerLogin.expiresIn);

      // 5. WebSocket 연결 초기화 (로그인 성공 후 subscription 사용 가능)
      initializeWebSocket();

      return {
        success: true,
        data: data.managerLogin
      };
    } catch (error) {
      console.error('Manager login failed:', error);
      return {
        success: false,
        error: this.parseError(error)
      };
    }
  }

  /**
   * 일반 사용자 로그인
   */
  async login(id, password) {
    if (!this.apolloClient) {
      throw new Error('Apollo Client not initialized');
    }

    try {
      // 1. 디바이스 ID 생성
      this.currentDeviceId = getBrowserFingerprint();
      const deviceInfo = getDeviceInfo();

      // 2. 로그인 요청
      const { data } = await this.apolloClient.mutate({
        mutation: LOGIN_MUTATION,
        variables: {
          input: {
            id,
            userPw: password,
            deviceId: this.currentDeviceId,
            deviceName: deviceInfo.deviceName,
            deviceType: deviceInfo.deviceType // 'DESKTOP'
          }
        },
        fetchPolicy: 'no-cache'
      });

      // 3. 토큰 및 사용자 정보 저장
      this.saveAuthData(data.login);

      // 4. 토큰 자동 갱신 설정
      this.startTokenRefreshTimer(data.login.expiresIn);

      // 5. WebSocket 연결 초기화 (로그인 성공 후 subscription 사용 가능)
      initializeWebSocket();

      return {
        success: true,
        data: data.login
      };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: this.parseError(error)
      };
    }
  }

  /**
   * 인증 데이터 저장
   */
  saveAuthData(authData) {
    // localStorage에 저장
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, authData.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, authData.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(authData.user));
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT,
      new Date(Date.now() + authData.expiresIn * 1000).toISOString()
    );

    // 관리자 전용 추가 정보
    localStorage.setItem(STORAGE_KEYS.USER_ID, authData.user.id);
    localStorage.setItem(STORAGE_KEYS.AUTHORITY, authData.user.authority.toString());
  }

  /**
   * 토큰 자동 갱신 타이머 설정
   */
  startTokenRefreshTimer(expiresIn) {
    // 기존 타이머 정리
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // 만료 5분 전에 갱신 (expiresIn은 초 단위)
    const refreshTime = (expiresIn - 300) * 1000; // 5분 = 300초

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken();
      }, refreshTime);
    }
  }

  /**
   * 토큰 갱신
   */
  async refreshToken() {
    if (!this.apolloClient) {
      console.error('Apollo Client not initialized');
      return;
    }

    try {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      // localStorage에서 deviceId 복원 (페이지 새로고침 대응)
      const deviceId = this.currentDeviceId || localStorage.getItem(STORAGE_KEYS.DEVICE_ID);

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      if (!deviceId) {
        throw new Error('No device ID available');
      }

      const { data } = await this.apolloClient.mutate({
        mutation: REFRESH_TOKEN_MUTATION,
        variables: {
          input: {
            refreshToken,
            deviceId
          }
        }
      });

      // 새 토큰 저장
      this.saveAuthData(data.refreshToken);

      // 다음 갱신 스케줄링
      this.startTokenRefreshTimer(data.refreshToken.expiresIn);

      return { success: true };
    } catch (error) {
      console.error('Token refresh failed:', error);
      // 갱신 실패 시 로그아웃 처리
      this.logout();
      return { success: false, error: error.message };
    }
  }


  /**
   * 로그아웃
   */
  async logout() {
    try {
      // deviceId 복원 (페이지 새로고침 후에도 정상 로그아웃 가능)
      const deviceId = this.currentDeviceId || localStorage.getItem(STORAGE_KEYS.DEVICE_ID);

      // 1. 서버에 로그아웃 요청
      if (this.apolloClient && deviceId) {
        await this.apolloClient.mutate({
          mutation: LOGOUT_MUTATION,
          variables: {
            deviceId: deviceId
          }
        });
      }

      // 2. WebSocket 연결 종료
      terminateWebSocket();

      // 3. 타이머 정리
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }

      // 4. 로컬 데이터 삭제
      this.clearAuthData();

      // 5. Apollo 캐시 초기화
      if (this.apolloClient) {
        await this.apolloClient.clearStore();
      }

      return { success: true };
    } catch (error) {
      console.error('Logout failed:', error);
      // 에러가 발생해도 WebSocket 종료 및 로컬 데이터 삭제
      terminateWebSocket();
      this.clearAuthData();
      return { success: false, error: error.message };
    }
  }

  /**
   * 인증 데이터 삭제
   */
  clearAuthData() {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
    localStorage.removeItem(STORAGE_KEYS.AUTHORITY);
    localStorage.removeItem(STORAGE_KEYS.DEVICE_ID);
    this.currentDeviceId = null;

    // 인증 상태 변경 이벤트 발생 (DashBoard에서 리스닝)
    window.dispatchEvent(new CustomEvent('auth-state-change', {
      detail: { type: 'logout' }
    }));
  }

  /**
   * 인증 상태 확인
   */
  isAuthenticated() {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);

    if (!token || !expiresAt) return false;

    return new Date(expiresAt) > new Date();
  }

  /**
   * 현재 사용자 정보 가져오기
   */
  getCurrentUser() {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (!userData) return null;

    try {
      return JSON.parse(userData);
    } catch (error) {
      console.error('Failed to parse user data:', error);
      return null;
    }
  }

  /**
   * 토큰 검증
   */
  async validateToken() {
    if (!this.apolloClient) {
      return { valid: false };
    }

    try {
      const { data } = await this.apolloClient.query({
        query: VALIDATE_TOKEN_QUERY,
        fetchPolicy: 'network-only'
      });

      return data.validateToken;
    } catch (error) {
      console.error('Token validation failed:', error);
      return { valid: false };
    }
  }

  /**
   * 에러 파싱
   */
  parseError(error) {
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const message = error.graphQLErrors[0].message;

      // 에러 메시지 한글화
      const errorMessages = {
        'Invalid credentials': '아이디 또는 비밀번호가 올바르지 않습니다.',
        'Account is disabled': '계정이 비활성화되었습니다.',
        'Password change required for new account': '신규 계정은 비밀번호 변경이 필요합니다.',
        'Service hours: Monday 08:00 ~ Saturday 07:30': '서비스 운영시간: 월요일 08:00 ~ 토요일 07:30',
        'Token expired': '세션이 만료되었습니다.',
        'Invalid token': '유효하지 않은 토큰입니다.'
      };

      return errorMessages[message] || message;
    }

    if (error.networkError) {
      return '네트워크 오류가 발생했습니다.';
    }

    return error.message || '알 수 없는 오류가 발생했습니다.';
  }
}

// 싱글톤 인스턴스
const authService = new AuthService();
export default authService;